import uniq from "lodash/uniq";

import Config from "../config";
import store from "../store";
import {
  restorePriceWorksheetState,
  setDiscountSections,
  setFinancePercentage,
  setWorkSheetsCount
} from "../actions/priceWorksheetActions";
import {getBvFeatures, getDiscounts} from "./server-api";
import {getArrayOfIdsFromBvState, sortByOrder} from "../misc/helpers";
import {setActiveQuoteIndex, setQuotes} from "../actions/roomsFunctions";
import {objectWithoutProperties} from "../misc/helpers";
import {createFakeLayout, getCurrentLayout, getVisualizerTypeFromBvScene} from "../bvcomponents/appLayout";
import {
  changeUserInfo,
  setFirstLoadedPreset, setStonePricing,
  setUserPhoneNumberValidated,
  toggleUserPopup
} from "../actions/actionFunctions";
import {getImagePromiseFromLayout} from "../misc/getCanvasImageFromLayout";
import Misc from "../misc/misc";
import {setAmazonImageUrl} from "../actions/serverFunctions";
import {restorePaymentInfo} from "../actions/paymentFunctions";
import Analytics from "../analytics";
import {restoreAllWizardSelectedValues, setWizardList} from "../actions/wizardFunctions";
import {getQuoteInfo} from "../misc/quotesFunctions";
import JsonApi from "./jsonApi";
import {getParsedWizardList} from "../bvcomponents/look/lookNavigation/lookMain/lookWizard/lookWizardFunctions2";
import {parseMarkUp} from "../misc/priceFunctions";
import {setMarkUpLogic} from "../actions/shoppingCartFunctions";
import {DISCOUNT_MAIN_TYPES} from "../bvcomponents/priceWorksheet/discounstInterface";
import {setContractIMEFieldsValues} from "../actions/contractsActions";
import {restoreIMEObjectKeys} from "../bvcomponents/popups/UIPopup/IMEFields/IMEFieldsFunctions";
import {toSaveAndRestoreWorksheetProperties} from "../bvcomponents/priceWorksheet/discountFunctions";

/**
 * Sets noteText for loaded features
 * @param notesFeatures {Array.<notesFeatureInterface>}
 * @param layout
 */
const setNotesToFeatures = (notesFeatures = [], layout) => {
  const parents = layout.getFeatures(true); // we need other groups too
  notesFeatures.forEach((notesFeature) => {
    parents.forEach(parent => {
      const subParts = parent.subParts();
      subParts.forEach(subPart => {
        Object.keys(subPart.part.featuresList).forEach(layoutName => {
          const featureFound = subPart.part.featuresList[layoutName].find(feature => {
            return feature.dbFeature.id === notesFeature.id
          })
          if (featureFound) {
            featureFound.notesList[notesFeature.currentQuote] = notesFeature.noteText;
            // console.log("featureFound", featureFound);
          }
        })
      });
    })
  })
};


/**
 * checks if we need to do multiple features requests
 * @param records {Array<{}>}
 * @param fabricatorState {object}
 * @param serverState {object}
 * @returns boolean
 */
const hasMultipleVisualizers = (records, fabricatorState, serverState) => {
  console.log("serverState.initialVisualizerType", serverState.initialVisualizerType);
  return fabricatorState.settings.allowVisualizerSwitching
    && records.some(room => {
      return room.data && room.data.visualizerType && (room.data.visualizerType !== serverState.initialVisualizerType)
    })
}

/**
 * when the new customer is created - the system creates a job for him with only one userState property
 * @param jobResponse {object}
 * @param bvState {object}
 * @returns {boolean}
 */
export const isNewlyCreatedJob = (jobResponse, bvState) => {

  if (jobResponse && !bvState) {
    if (Array.isArray(jobResponse.records) && jobResponse.records.length) {
      const records = jobResponse.records.filter(record => record && record.data);
      if (records.length === 1) {
        bvState = records[0].data;
      }
    } else { // whoa, nothing there, so it's probably a brand new job, let's load default preset
      return true
    }
  }

  return bvState && (Object.keys(bvState).length === 1) && bvState.userState
}

/**
 * loads features and notes and also stone pricings
 * @param records {Array<{}>}
 * @param activeQuoteIndex {number}
 * @returns {Promise<array>} loaded features
 */
const loadFeaturesAndNotesForMultipleLayouts = async (records, activeQuoteIndex) => {
  let featuresToReturn = [];

  const differentVisualizers = {};
  records.forEach((record, recordIndex) => {
    if (!differentVisualizers[record.data.visualizerType]) {
      differentVisualizers[record.data.visualizerType] = {
        arrayOfFeaturesIds: [],
        notesFeatures: [],
      }
    }
    const currentObject = differentVisualizers[record.data.visualizerType];
    record.features.forEach((feature) => {
      if (feature.fabricatorFeature_id) {
        if (!currentObject.arrayOfFeaturesIds.includes(feature.fabricatorFeature_id)) {
          currentObject.arrayOfFeaturesIds.push(feature.fabricatorFeature_id);
        }
        if (feature.noteText) {
          currentObject.notesFeatures.push({
            id: feature.fabricatorFeature_id,
            noteText: feature.noteText,
            currentQuote: recordIndex
          })
        }
      }
    });
    currentObject.arrayOfFeaturesIds = uniq([
      ...currentObject.arrayOfFeaturesIds, // here we add all cartItems features
      ...getArrayOfIdsFromBvState(record.data) // and then we add all active features from the quotes
    ]);
  });
  for (const visualizerType of Object.keys(differentVisualizers)) {
    const currentObject = differentVisualizers[visualizerType];
    const forLayout = getCurrentLayout(visualizerType);
    const featuresFromJob = await getBvFeatures(currentObject.arrayOfFeaturesIds, null, false, visualizerType);
    if (featuresFromJob && featuresFromJob.data) {
      // console.log("featuresFromJob", featuresFromJob.data);
      const nextBvState = records[activeQuoteIndex] ? records[activeQuoteIndex].data : null;
      forLayout.loadNewFeatures(featuresFromJob.data, null, true, nextBvState);
      if (visualizerType === document.kvConfig.visualizerType) {
        featuresToReturn = featuresFromJob.data;
      }
    }
    setNotesToFeatures(currentObject.notesFeatures, forLayout) // here we set notes

    // we also need stone pricing!! to be able to show correct price
    if (visualizerType !== document.kvConfig.visualizerType) { // skip current, it was already downloaded
      const mainResponse = await JsonApi.getBathroomVisualizerData(30, visualizerType);
      console.log("mainResponse for stonePricing", mainResponse);
      if (mainResponse && mainResponse.data && mainResponse.data.stonePricing) {
        store.dispatch(setStonePricing(mainResponse.data.stonePricing)); // we need to set stone pricing for cart features
      }
      if (mainResponse && mainResponse.data && (mainResponse.data.wizardData || mainResponse.data.wizardDataV2)) {
        const wizardList = getParsedWizardList(mainResponse.data, false, visualizerType);
        store.dispatch(setWizardList(wizardList));
      }
    }
  }

  return featuresToReturn
}

const getBvStateProperty = (responseData, activeQuoteIndex, property = "") => {
  if (!responseData.records || !Array.isArray(responseData.records) || !responseData.records.length) {
    return null
  }
  return responseData.records[0].data[property] ||
  responseData.records[activeQuoteIndex] && responseData.records[activeQuoteIndex].data
    ? responseData.records[activeQuoteIndex].data[property]
    : null;
}

export const parseJobIdResponse = async (responseData, isDesignOnly = false) => {
  const {fabricatorState, serverState} = store.getState();
  const layout = getCurrentLayout();
  if (Config.isDevMode()) {
    console.log(`getJobId ${document.kvConfig.jobId}:`, responseData);
  }
  let activeQuoteIndex = 0;
  if (Array.isArray(responseData.records) && responseData.records.length) {
    responseData.records = responseData.records.filter(record => record && record.data);
    const firstRecord = responseData.records[0];
    if (firstRecord) {
      activeQuoteIndex = firstRecord.data.activeQuoteIndex || 0;
      let loadedFeaturesFromJob = [];
      if (hasMultipleVisualizers(responseData.records, fabricatorState, serverState)) {
        loadedFeaturesFromJob = await loadFeaturesAndNotesForMultipleLayouts(responseData.records, activeQuoteIndex);
      } else {
        let notesFeatures = [];
        let arrayOfFeaturesIds = [];
        responseData.records.forEach((rec, recIndex) => {
          if (Array.isArray(rec.features) && rec.features.length) {
            rec.features.forEach((feature) => {
              if (feature.fabricatorFeature_id) {
                arrayOfFeaturesIds.push(feature.fabricatorFeature_id)
                if (feature.noteText) {
                  notesFeatures.push({
                    id: feature.fabricatorFeature_id,
                    noteText: feature.noteText,
                    currentQuote: recIndex
                  })
                }
              }
            })
          }
        });
        responseData.records.forEach((rec) => {
          arrayOfFeaturesIds = uniq([
            ...arrayOfFeaturesIds, // here we add all cartItems features
            ...getArrayOfIdsFromBvState(rec.data) // and then we add all active features from the quotes
          ])
        })
        const featuresFromJob = await getBvFeatures(arrayOfFeaturesIds);
        if (featuresFromJob && featuresFromJob.data) {
          loadedFeaturesFromJob = featuresFromJob.data;
          // console.log("featuresFromJob", featuresFromJob.data);
          const nextBvState = responseData.records[activeQuoteIndex] ? responseData.records[activeQuoteIndex].data : null;
          layout.loadNewFeatures(featuresFromJob.data, null, true, nextBvState);
        }
        setNotesToFeatures(notesFeatures, layout) // here we set notes
      }

      // restore wizard before the cart! to make sure cart is restored with proper display logic items that need wizard selections
      const allWizardValues = getBvStateProperty(responseData, activeQuoteIndex, "allWizardsValues");
      if (allWizardValues) {
        await store.dispatch(restoreAllWizardSelectedValues([...allWizardValues]));
        delete firstRecord.data.allWizardsValues; // we don't need it anymore, prevent overwriting
        if (responseData.records[activeQuoteIndex] && responseData.records[activeQuoteIndex].data && responseData.records[activeQuoteIndex].data.allWizardsValues) {
          delete responseData.records[activeQuoteIndex].data.allWizardsValues
        }
      }

      if (responseData.records[activeQuoteIndex] && responseData.records[activeQuoteIndex].data) { // just in case
        const restoreCartFeatures = isDesignOnly ? false : loadedFeaturesFromJob
        const nextBvState = responseData.records[activeQuoteIndex].data;
        if (isNewlyCreatedJob(null, nextBvState)) {
          if (restoreCartFeatures) {
            layout.setCartStateAndUserStateFromBvState(nextBvState);
          }
        } else {
          layout.setActiveFeaturesFromObject(
            responseData.records[activeQuoteIndex].data,
            restoreCartFeatures,
            false,
            true,
            true,
            false
          );  // here we set bvState! and restore cart!!
        }
      }
      if (firstRecord.imageUrl) {
        store.dispatch(setAmazonImageUrl(firstRecord.imageUrl));
      }
      if (firstRecord.data.id) {
        store.dispatch(setFirstLoadedPreset(firstRecord.data.id));
      }
    }

    let newQuotes = [];
    for (const [recordIndex, record] of responseData.records.entries()) {
      //1) detect visualizer layout
      const visType = getVisualizerTypeFromBvScene(record.data, record);

      record.room = visType; // this might change coming incorrect visualizer!

      let image = "";
      if (responseData.records.length === 1 || (recordIndex === activeQuoteIndex)) {
        image = await getImagePromiseFromLayout(layout, null, 0.7, true);
      } else {
        let fakeLayout = createFakeLayout(getCurrentLayout(visType));
        let bvState = record.data;
        if (responseData.records.length === 1 && Object.keys(bvState).length <= 2) { // new created quote
          bvState = layout.getActiveFeaturesAsObject(false, false, true);
        }
        //2) set visualizer layout features
        await fakeLayout.setItemsFromBvState(bvState, false, null, true);
        //3) create quote image
        image = await getImagePromiseFromLayout(fakeLayout, null, 0.7, true);
      }

      //4) set quote other info
      let quoteObject = await getQuoteInfo(recordIndex, null, false, "", image);

      quoteObject.quoteId = record.id;
      quoteObject.data = objectWithoutProperties(record.data, ["installations", "cartState", "userState", "allWizardsValues"]);
      quoteObject.layoutType = record.data.layout_type;
      quoteObject.room = record.room;
      quoteObject.sqFtCalcData = record.sqFtCalcData;
      quoteObject.bvImage = image;
      quoteObject.roomMultiplier = record.data.roomMultiplier || 1;
      newQuotes.push(quoteObject);
    }

    if (newQuotes.length) {
      store.dispatch(setQuotes([...newQuotes]));
      if (newQuotes[activeQuoteIndex]) {
        store.dispatch(setActiveQuoteIndex(activeQuoteIndex));
      }
    }
  }
  if (Array.isArray(responseData.discountSections) && responseData.discountSections.length && !isDesignOnly) {
    await getDiscounts();
    let allDiscountSections = store.getState().priceWorksheetState.discountSections;
    // console.log("allDiscountSections", allDiscountSections);
    // console.log("responseData.discountSections", responseData.discountSections);
    const priceWorkSheetLogic = getBvStateProperty(responseData, activeQuoteIndex, "priceWorkSheetLogic");
    if (priceWorkSheetLogic) {
      if (toSaveAndRestoreWorksheetProperties.some(property => {
        return (property === "customAddedDiscounts")
          ? Array.isArray(priceWorkSheetLogic.customAddedDiscounts) && priceWorkSheetLogic.customAddedDiscounts.length
          : priceWorkSheetLogic[property]
      })) {
        store.dispatch(restorePriceWorksheetState({
          ...toSaveAndRestoreWorksheetProperties.reduce((acc, property) => {
            if (!priceWorkSheetLogic[property]) {
              return acc
            }
            return { ...acc, [property]: priceWorkSheetLogic[property] };
          }, {})
        }));
      }
      if (priceWorkSheetLogic.financePercentage) {
        store.dispatch(setFinancePercentage(priceWorkSheetLogic.financePercentage));
      }
      if (priceWorkSheetLogic.selectedDiscounts) {
        let notRestoredSelectedDiscounts = [...priceWorkSheetLogic.selectedDiscounts];
        allDiscountSections = allDiscountSections.filter(ds => ds.id !== 4030).map((discountSection) => {
          discountSection.discounts = discountSection.discounts.map(discount => {
            const sameDiscountSaved = priceWorkSheetLogic.selectedDiscounts.find(dis => {
              return (discount.uuId && dis.uuId && (discount.uuId === dis.uuId))
                || (discount.id === dis.id && (discountSection.id === dis.categoryId))
            });
            if (sameDiscountSaved) {
              discount.selected = true;
              if (discount.isRidged) {
                discount.name = sameDiscountSaved.name;
                discount.amount = sameDiscountSaved.amount;
                discount.approveOptions = sameDiscountSaved.approveOptions;
              } else if (sameDiscountSaved.discountType === DISCOUNT_MAIN_TYPES.deposit
                || sameDiscountSaved.discountType === DISCOUNT_MAIN_TYPES.downPayment) {
                discount.amount = sameDiscountSaved.amount;
                discount.type = sameDiscountSaved.type; // if it was percentage - we convert it to price
              } else if (sameDiscountSaved.discountType === DISCOUNT_MAIN_TYPES.finance) {
                discount.financeOptions = sameDiscountSaved.financeOptions
              }
              if (sameDiscountSaved.roomValues || sameDiscountSaved.defaultValues) {
                discount.roomValues = sameDiscountSaved.roomValues;
                discount.defaultValues = sameDiscountSaved.defaultValues;
              }
              notRestoredSelectedDiscounts = notRestoredSelectedDiscounts.filter(dis => dis.uuId !== discount.uuId);
            }
            return discount
          })
          if (notRestoredSelectedDiscounts.length) {
            notRestoredSelectedDiscounts.forEach(savedDiscount => {
              if (savedDiscount.discountType !== DISCOUNT_MAIN_TYPES.lowesIME) {
                return // do not restore other discounts
              }
              if (savedDiscount.categoryId === discountSection.id) {
                discountSection.discounts.push(savedDiscount);
                notRestoredSelectedDiscounts = notRestoredSelectedDiscounts.filter(dis => dis.uuId !== savedDiscount.uuId);
              }
            })
          }
          return discountSection
        });
        if (notRestoredSelectedDiscounts.length) {
          notRestoredSelectedDiscounts.forEach(savedDiscount => {
            if (savedDiscount.discountType !== DISCOUNT_MAIN_TYPES.lowesIME) {
              return // do not restore other discounts
            }
            const existingDiscountSection = allDiscountSections.find(section => section.id === savedDiscount.categoryId);
            if (existingDiscountSection) {
              existingDiscountSection.discounts.push(savedDiscount);
            } else {
              allDiscountSections.push({
                id: savedDiscount.categoryId,
                name: savedDiscount.categoryName || "Other",
                order: savedDiscount.categoryOrder || 0,
                wizardLogic: savedDiscount.wizardLogic || "",
                discounts: [savedDiscount]
              });
            }
            notRestoredSelectedDiscounts = notRestoredSelectedDiscounts.filter(dis => dis.uuId !== savedDiscount.uuId);
          })
        }
      }
    } else {
      responseData.discountSections.forEach((selectedDiscountSection) => {
        let discountSectionFound = false;
        allDiscountSections = allDiscountSections.map((discountSection) => {
          if (selectedDiscountSection.id === discountSection.id || selectedDiscountSection.name === discountSection.name) {
            selectedDiscountSection.discounts.forEach(selectedDiscount => {
              let discountFound = false;
              discountSection.discounts.map(discount => {
                if (selectedDiscount.id === discount.id
                  || ["name", "amount"].every(property => selectedDiscount[property] === discount[property])
                  || ["isRidged", "mode", "order", "type"].every(property => selectedDiscount[property] === discount[property])) {
                  discount.selected = true;
                  discountFound = true;
                  if (discount.isRidged) {
                    discount.name = selectedDiscount.name;
                    discount.amount = selectedDiscount.amount;
                  }
                }
                return discount
              });
              if (!discountFound) {
                console.log("No discount found! Pls call administrator"); // haha funny. this happens when discounts are changed, and old quotes opened
                selectedDiscount.id = Misc.getRandomInt(0, 9999999999);
                selectedDiscount.oldDeleted = true;
                discountSection.discounts.push(selectedDiscount)
              }
            })
            discountSectionFound = true;
          }
          return discountSection
        });
        if (!discountSectionFound) {
          selectedDiscountSection.discounts.forEach(disc => {
            disc.oldDeleted = true;
          })
          allDiscountSections.push(selectedDiscountSection)
        }
      })
    }
    const discountSections = sortByOrder(allDiscountSections.map(discountSection => {
      discountSection.discounts = sortByOrder(discountSection.discounts);
      return discountSection
    }));
    store.dispatch(setDiscountSections(discountSections));
    store.dispatch(setWorkSheetsCount(discountSections));
  }
  const lastSavedRecord = responseData.records && responseData.records[activeQuoteIndex]
    && responseData.records[activeQuoteIndex].data ? responseData.records[activeQuoteIndex].data : null;
  if (responseData.user && responseData.user.values && !isDesignOnly) { // update user state
    const savedCountry = responseData.records
    && lastSavedRecord
    && lastSavedRecord.userState
    && lastSavedRecord.userState.userInfo
    && lastSavedRecord.userState.userInfo.shipping
    && lastSavedRecord.userState.userInfo.shipping.country
      ? lastSavedRecord.userState.userInfo.shipping.country
      : responseData.user.shippingAddress ? responseData.user.shippingAddress.country : "";
    const newUserInfo = Misc.parseUserResponse(responseData.user, savedCountry);
    if (Config.isDevMode()) {
      console.log("parseResponse newUserInfo", newUserInfo);
    }
    store.dispatch(changeUserInfo(newUserInfo));
    store.dispatch(setUserPhoneNumberValidated(true));
    if (responseData.user.encodedUserId) {
      Analytics.setUserId(responseData.user.encodedUserId);
      store.dispatch(toggleUserPopup({
        encodedUserId: responseData.user.encodedUserId,
      }));
    }
  }
  if (lastSavedRecord && ((lastSavedRecord.billingAddress && lastSavedRecord.billingAddress.address1)
    || (lastSavedRecord.shippingAddress && lastSavedRecord.shippingAddress.address1))) { // restore first from saved job!
    store.dispatch(restorePaymentInfo(Misc.parseShippingAndBillingResponse(lastSavedRecord)));
  } else if (responseData.user && (responseData.user.billingAddress || responseData.user.shippingAddress)) { // update user state
    store.dispatch(restorePaymentInfo(Misc.parseShippingAndBillingResponse(responseData.user)));
  }
  if (Array.isArray(responseData.imeFieldsValues)) {
    store.dispatch(setContractIMEFieldsValues(responseData.imeFieldsValues.map(IMEObject => {
      return restoreIMEObjectKeys(IMEObject)
    })))
  }
  if (responseData.jobMarkUp) {// restore mark up
    const globalMarkUp = parseMarkUp(responseData.jobMarkUp);
    if (globalMarkUp) {
      store.dispatch(setMarkUpLogic(globalMarkUp)) // override cartState, because it was changed in the job board
    }
  }
}
